import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useRecoilCallback } from 'recoil';
import { adminToolbarScope } from '@sd/lib/ticketListFilterStorage';
import { ticketToolbarFiltersAtomFamily } from '@sd/state/ticketToolbarFiltersState';
import { A1EmbedNavigationProvider } from '@/context/A1EmbedNavigationContext';
import SchemasListPage from '@/pages/SchemaManagement/SchemasListPage';
import ImportListPage from '@/pages/ImportManagement/ImportListPage';
import PermissionPage from '@/pages/UserManagement/PermissionPage';
import RoleListPage from '@/pages/UserManagement/RoleListPage';
import UserListPage from '@/pages/UserManagement/UserListPage';
import { AdminLayout } from '@sd/components/admin/AdminLayout';
import { sdA1EmbedNavConfig } from '@sd/lib/sdA1EmbedNav';
import { AdminDashboard } from '@sd/components/admin/AdminDashboard';
import { ProjectsIndex } from '@sd/components/projects/ProjectsIndex';
import { AdminProjectStatsDashboard } from '@sd/components/admin/AdminProjectStatsDashboard';
import { AdminTicketManagement } from '@sd/components/admin/AdminTicketManagement';
import { AdminUserManagement } from '@sd/components/admin/AdminUserManagement';
import { TrackerReportDashboard } from '@sd/components/reports/TrackerReportDashboard';
import { AdminSettings } from '@sd/components/admin/AdminSettings';
import { AdminActivityLog } from '@sd/components/admin/AdminActivityLog';
// import { AdminCompanies } from '@sd/components/admin/AdminCompanies';
// import { AdminEmailInbox } from '@sd/components/admin/AdminEmailInbox';
import { AdminKanban } from '@sd/components/admin/AdminKanban';
import { AdminMergeTickets } from '@sd/components/admin/AdminMergeTickets';
import { AdminChatMeetings } from '@sd/components/admin/AdminChatMeetings';
import { AdminChatGroups } from '@sd/components/admin/AdminChatGroups';
import { AdminChatDM } from '@sd/components/admin/AdminChatDM';
// import { AdminChatMeetingsPage } from '@sd/components/admin/AdminChatMeetingsPage';  // TODO: enable later
import { AdminChatCalls } from '@sd/components/admin/AdminChatCalls';
import { AdminNotifications } from '@sd/components/admin/AdminNotifications';
import { AdminEmployees } from '@sd/components/admin/AdminEmployees';
import { AdminGuestEndUserListing } from '@sd/components/admin/AdminGuestEndUserListing';
import { AdminAgentsPendingPage } from '@sd/components/admin/AdminAgentsPendingPage';
import SLAConfigurationListPage from '@/pages/SLAConfiguration';
import SLAFormPage from '@/pages/SLAConfiguration/SLA/SLAFormPage';
import SLASchemaConfigurationPage from '@/pages/SLAConfiguration/SLASchemaConfiguration';
import SLASchemaConfigurationListPage from '@/pages/SLAConfiguration/SLASchemaConfiguration/SLASchemaConfigurationListPage';
import { cn } from '@/lib/utils';

const A1_EMBED_VIEWS = new Set([
  'a1-schemas',
  'a1-imports',
  'a1-permission',
  'a1-roles',
  'a1-tenant-users',
]);

const A1_EMBED_RESPONSIVE_VIEWS = new Set([
  'a1-schemas',
  'a1-imports',
  'a1-permission',
  'a1-roles',
  'a1-tenant-users',
  'sla-configuration',
  'sla-configuration-add',
  'sla-configurations-add',
  'sla-configuration-schema',
]);

const isA1EmbedView = (view: string | undefined): boolean =>
  view != null &&
  (A1_EMBED_VIEWS.has(view) ||
    view.startsWith('sla-configuration') ||
    view.startsWith('sla-configurations'));

export default function Admin() {
  const { view } = useParams<{ view?: string }>();
  const [searchParams] = useSearchParams();
  const useA1Embed = isA1EmbedView(view);
  const prevViewRef = useRef<string | undefined>(view);

  const resetAdminTicketsToolbar = useRecoilCallback(
    ({ reset }) =>
      () => {
        reset(ticketToolbarFiltersAtomFamily(adminToolbarScope()));
      },
    []
  );

  useEffect(() => {
    const prev = prevViewRef.current;
    if (prev === 'tickets' && view !== 'tickets') {
      resetAdminTicketsToolbar();
    }
    prevViewRef.current = view;
  }, [view, resetAdminTicketsToolbar]);

  const renderContent = () => {
    switch (view) {
      case 'a1-schemas':
        return <SchemasListPage />;
      case 'a1-imports':
        return <ImportListPage />;
      case 'a1-permission':
        return <PermissionPage />;
      case 'a1-roles':
        return <RoleListPage />;
      case 'a1-tenant-users':
        return <UserListPage />;
      case 'project-status':
        return <AdminProjectStatsDashboard panel="admin" />;
      case 'projects':
        return <ProjectsIndex />;
      case 'tickets': return <AdminTicketManagement />;
      case 'users': return <AdminUserManagement />;
      case 'activity': return <AdminActivityLog />;
      case 'report':
      case 'reports':
        return (
          <div className="min-h-0 min-w-0">
            <TrackerReportDashboard />
          </div>
        );
      case 'settings': return <AdminSettings />;
      // case 'companies': return <AdminCompanies />;
      case 'agents-pending': return <AdminAgentsPendingPage />;
      case 'employees': return <AdminEmployees />;
      case 'notifications': return <AdminNotifications />;
      // case 'inbox': return <AdminEmailInbox />;
      case 'kanban':
        return (
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <AdminKanban enableFilters />
          </div>
        );
      case 'merge': return <AdminMergeTickets />;
      case 'guest': return <AdminGuestEndUserListing />;
      case 'chat': return <AdminChatMeetings />;
      case 'chat-groups': return <AdminChatGroups />;
      case 'chat-dm': return <AdminChatDM />;
      // case 'chat-meetings': return <AdminChatMeetingsPage />;  // TODO: enable later
      case 'chat-calls': return <AdminChatCalls />;
      case 'sla-configuration': return <SLASchemaConfigurationListPage />;
      case 'sla-configurations': return <SLAConfigurationListPage />;
      case 'sla-configuration-add':
      case 'sla-configurations-add': return <SLASchemaConfigurationPage />;
      case 'sla-configuration-edit': return <SLAFormPage editId={searchParams.get('id') ?? undefined} />;
      case 'sla-configuration-schema': return <SLASchemaConfigurationPage />;
      default: return <AdminDashboard />;
    }
  };

  return (
    <AdminLayout>
      <A1EmbedNavigationProvider value={sdA1EmbedNavConfig}>
        {useA1Embed ? (
          <div
            className={cn(
              "min-h-full bg-[#f5f5f5]",
              A1_EMBED_RESPONSIVE_VIEWS.has(view ?? "")
                ? "min-w-0 p-4 sm:p-6"
                : "p-6"
            )}
          >
            {renderContent()}
          </div>
        ) : (
          renderContent()
        )}
      </A1EmbedNavigationProvider>
    </AdminLayout>
  );
}
